import React, { useMemo, useState } from 'react';
import { Trophy, Search } from 'lucide-react';
import { Participant } from '../../interface/HallOfFame';
import { useTheme } from '../ThemeContext';

interface LeaderboardProps {
  participants: Participant[];
  communityAverageROI: number;
  onSelectParticipant: (participant: Participant) => void;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({
  participants,
  communityAverageROI,
  onSelectParticipant,
}) => {
  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Filter participants based on search and tier (both filters applied simultaneously)
  const filteredParticipants = useMemo(() => {
    return participants.filter((p) => {
      // Tier filter - handle case insensitivity and 'all' option
      const participantTier = (p.tier || '').toUpperCase();
      const filterTier = selectedTier.toUpperCase();
      const matchesTier = filterTier === 'ALL' || participantTier === filterTier;

      // Search filter - handle null/undefined values safely
      let matchesSearch = true;
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const name = (p.name || '').toLowerCase();
        const socialHandle = (p.socialHandle || '').toLowerCase();
        const hasMatchingStock = Array.isArray(p.stockPicks) &&
          p.stockPicks.some((s) => (s.symbol || '').toLowerCase().includes(term));

        matchesSearch = name.includes(term) || socialHandle.includes(term) || hasMatchingStock;
      }

      // Both conditions must be true
      return matchesTier && matchesSearch;
    });
  }, [participants, searchTerm, selectedTier]);

  // Sort by ROI descending
  const sortedParticipants = useMemo(() => {
    return [...filteredParticipants].sort((a, b) => (b.totalROI ?? 0) - (a.totalROI ?? 0));
  }, [filteredParticipants]);

  // Pagination
  const totalPages = Math.ceil(sortedParticipants.length / itemsPerPage);
  const paginatedParticipants = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedParticipants.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedParticipants, currentPage]);

  const getTierBadge = (tier: string) => {
    const configs: { [key: string]: { bg: string; text: string; icon: string } } = {
      S: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', icon: '👑' },
      A: { bg: 'bg-gray-200 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', icon: '🥈' },
      B: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: '🥉' },
      C: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: '📊' },
      D: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: '⚠️' },
      F: { bg: 'bg-red-200 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', icon: '💀' },
    };
    return configs[tier] || configs.F;
  };

  const getTierDefinition = (tier: string) => {
    const definitions: { [key: string]: string } = {
      S: '+50% or higher (Exceptional performance)',
      A: '+30% to +49.99% (Excellent performance)',
      B: '+20% to +29.99% (Good performance)',
      C: '+0% to +19.99% (Average performance)',
      D: '-20% to -0.01% (Below average performance)',
      F: 'Below -20% (Poor performance)',
    };
    return definitions[tier] || definitions.F;
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const tierCounts = useMemo(() => {
    const counts: { [key: string]: number } = { all: participants.length };
    participants.forEach((p) => {
      counts[p.tier] = (counts[p.tier] || 0) + 1;
    });
    return counts;
  }, [participants]);

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-theme-secondary rounded-lg p-3 md:p-4 border border-theme-tertiary">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-muted" size={18} />
          <input
            type="text"
            placeholder="Search by name, handle, or ticker symbol..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2.5 md:py-3 bg-theme-tertiary border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-theme-primary placeholder-theme-muted text-sm md:text-base"
          />
        </div>
      </div>

      {/* Community Average Banner */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-500 rounded-lg p-3 md:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <div className="w-1 h-10 md:h-12 bg-purple-500 flex-shrink-0"></div>
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-theme-primary truncate">Community Average</h3>
              <p className="text-xs md:text-sm text-theme-muted">Peer-to-Peer Benchmark</p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xl md:text-2xl font-bold text-purple-600 dark:text-purple-400">{formatPercent(communityAverageROI)}</div>
            <div className="text-xs text-theme-muted whitespace-nowrap">Average Return</div>
          </div>
        </div>
      </div>

      {/* Tier Filters */}
      <div className="flex flex-wrap gap-1.5 md:gap-2">
        <button
          onClick={() => {
            setSelectedTier('all');
            setCurrentPage(1);
          }}
          className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${
            selectedTier === 'all'
              ? 'bg-blue-500 text-white'
              : 'bg-theme-secondary text-theme-primary border border-theme-tertiary hover:bg-theme-tertiary'
          }`}
        >
          All ({tierCounts.all || 0})
        </button>
        {['S', 'A', 'B', 'C', 'D', 'F'].map((tier) => {
          const config = getTierBadge(tier);
          return (
            <button
              key={tier}
              onClick={() => {
                setSelectedTier(tier);
                setCurrentPage(1);
              }}
              title={getTierDefinition(tier)}
              className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all cursor-pointer ${
                selectedTier === tier
                  ? `${config.bg} ${config.text}`
                  : 'bg-theme-secondary text-theme-primary border border-theme-tertiary hover:bg-theme-tertiary'
              }`}
            >
              {config.icon} {tier}-Tier ({tierCounts[tier] || 0})
            </button>
          );
        })}
      </div>

      {/* Results count */}
      <div className="text-sm text-theme-muted">
        Showing {paginatedParticipants.length} of {sortedParticipants.length} investors
        {selectedTier !== 'all' && ` in ${selectedTier}-Tier`}
        {searchTerm && ` matching "${searchTerm}"`}
        {(searchTerm || selectedTier !== 'all') && ` (filtered from ${participants.length} total)`}
      </div>

      {/* Table - Scrollable on Mobile */}
      <div className="bg-theme-secondary rounded-lg border border-theme-tertiary overflow-hidden">
        {/* Mobile Scroll Hint */}
        <div className="md:hidden bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-3 py-2 text-xs text-blue-600 dark:text-blue-400 text-center">
          ← Swipe to see all columns →
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-theme-tertiary/50 sticky top-0 z-10">
              <tr className="text-left text-sm font-semibold text-theme-primary border-b border-theme-tertiary">
                <th className="px-4 py-3 w-16">Rank</th>
                <th className="px-4 py-3 min-w-[150px]">Investor</th>
                <th className="px-4 py-3 w-20">Tier</th>
                <th className="px-4 py-3 text-right min-w-[110px]">Portfolio</th>
                <th className="px-4 py-3 text-right min-w-[90px]">ROI</th>
                <th className="px-4 py-3 text-right min-w-[90px]">vs Avg</th>
                <th className="px-4 py-3 min-w-[130px]">Top Pick</th>
                <th className="px-4 py-3 text-right min-w-[90px]">Locked</th>
              </tr>
            </thead>
            <tbody>
              {paginatedParticipants.map((participant) => {
                const globalRank = [...participants]
                  .sort((a, b) => (b.totalROI ?? 0) - (a.totalROI ?? 0))
                  .findIndex((p) => p.id === participant.id) + 1;
                const tierConfig = getTierBadge(participant.tier || 'F');
                const topStock = Array.isArray(participant.stockPicks) && participant.stockPicks.length > 0
                  ? [...participant.stockPicks].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0))[0]
                  : null;

                return (
                  <tr
                    key={participant.id}
                    onClick={() => onSelectParticipant(participant)}
                    className="border-b border-theme-tertiary hover:bg-theme-tertiary/50 cursor-pointer transition-colors"
                  >
                    {/* Rank */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        {globalRank === 1 ? (
                          <Trophy className="text-yellow-500" size={20} />
                        ) : (
                          <span className="font-bold text-theme-primary">#{globalRank}</span>
                        )}
                      </div>
                    </td>

                    {/* Investor */}
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-semibold text-theme-primary">{participant.name}</div>
                        <div className="text-xs text-theme-muted">{participant.socialHandle}</div>
                      </div>
                    </td>

                    {/* Tier */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${tierConfig.bg} ${tierConfig.text} cursor-help`}
                        title={getTierDefinition(participant.tier || 'F')}
                      >
                        {tierConfig.icon} {participant.tier}
                      </span>
                    </td>

                    {/* Portfolio */}
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-theme-primary">
                        ${(participant.currentPortfolioValue ?? 0).toLocaleString()}
                      </div>
                    </td>

                    {/* ROI */}
                    <td className="px-4 py-3 text-right">
                      <div className={`font-bold ${(participant.totalROI ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatPercent(participant.totalROI ?? 0)}
                      </div>
                    </td>

                    {/* vs Community */}
                    <td className="px-4 py-3 text-right">
                      <div className={`text-sm font-medium ${
                        (participant.vsAverage ?? 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatPercent(participant.vsAverage ?? 0)}
                      </div>
                    </td>

                    {/* Top Stock */}
                    <td className="px-4 py-3">
                      {topStock ? (
                        <div className="text-sm">
                          <span className="font-mono font-semibold text-theme-primary">{topStock.symbol}</span>
                          <span className={`ml-2 ${topStock.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatPercent(topStock.roi)}
                          </span>
                        </div>
                      ) : (
                        <div className="text-sm text-theme-muted">No picks</div>
                      )}
                    </td>

                    {/* Lock Date */}
                    <td className="px-4 py-3 text-right text-xs text-theme-muted">
                      {participant.lockInDate ? new Date(participant.lockInDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-theme-tertiary/30 px-3 md:px-4 py-3 border-t border-theme-tertiary flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs md:text-sm text-theme-muted">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex-1 sm:flex-none px-3 md:px-4 py-2 rounded bg-theme-secondary text-theme-primary border border-theme-tertiary hover:bg-theme-tertiary disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex-1 sm:flex-none px-3 md:px-4 py-2 rounded bg-theme-secondary text-theme-primary border border-theme-tertiary hover:bg-theme-tertiary disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {sortedParticipants.length === 0 && (
        <div className="text-center py-12 text-theme-muted">
          <Search size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No investors found matching your criteria</p>
          {searchTerm && selectedTier !== 'all' && (
            <p className="text-sm mb-4">
              No one in <strong>{selectedTier}-Tier</strong> matches "<strong>{searchTerm}</strong>"
            </p>
          )}
          {searchTerm && selectedTier === 'all' && (
            <p className="text-sm mb-4">
              No one matches "<strong>{searchTerm}</strong>"
            </p>
          )}
          {!searchTerm && selectedTier !== 'all' && (
            <p className="text-sm mb-4">
              No investors in <strong>{selectedTier}-Tier</strong> yet
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
            {searchTerm && selectedTier !== 'all' && (
              <button
                onClick={() => setSelectedTier('all')}
                className="text-blue-500 hover:underline text-sm"
              >
                Search in all tiers instead
              </button>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedTier('all');
                setCurrentPage(1);
              }}
              className="text-blue-500 hover:underline text-sm"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

